import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../components/BopExcelTable';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
interface UltimaCondizione {
  id: number;
  dataDecorrenza: string;
  tassoDebitore: number | null;
  tassoCreditore: number | null;
  speseTenutaConto: number | null;
  commissioneDF: number | null;
  periodicitaCC: string | null;
  fidoImporto: number | null;
  fidoTasso: number | null;
  fidoScadenza: string | null;
  anticipoPercentuale: number | null;
  anticipoTasso: number | null;
  anticipoCommissioni: number | null;
}
interface Banca {
  id: number;
  societaId: number;
  societa: { id: number; nome: string };
  nome: string;
  abi: string | null;
  cab: string | null;
  iban: string | null;
  attiva: boolean;
  createdAt: string;
  updatedAt: string;
  ultimaCondizione: UltimaCondizione | null;
  haSottoconto: boolean;
  contoCoge: { id: number; codice: string; descrizione: string } | null;
}
interface GruppoContabile {
  id: number;
  codice: string;
  descrizione: string;
  livello: number;
}
interface SottocontoDisponibile {
  id: number;
  codice: string;
  descrizione: string;
}

// ── Helper: valida IBAN italiano ──────────────────────────────────────────
function validaIBAN(iban: string): boolean {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();
  return /^IT\d{2}[A-Z0-9]{23}$/.test(cleaned);
}

// ── Helper: formatta IBAN con spazi ogni 4 caratteri ─────────────────────
function formatIBANDisplay(iban: string): string {
  const cleaned = iban.replace(/\s/g, '');
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}

// ── Helper: IBAN mascherato (ultimi 4 visibili) ───────────────────────────
function maskIBAN(iban: string): string {
  const cleaned = iban.replace(/\s/g, '');
  if (cleaned.length <= 4) return cleaned;
  const visible = cleaned.slice(-4);
  const masked = '•'.repeat(cleaned.length - 4) + visible;
  return masked.match(/.{1,4}/g)?.join(' ') ?? masked;
}

// ── Helper: formatta input IBAN con spazi automatici ─────────────────────
function formatIBANInput(val: string): string {
  const cleaned = val.replace(/\s/g, '').toUpperCase();
  return cleaned.match(/.{1,4}/g)?.join(' ') ?? cleaned;
}

// ── Algoritmo generazione IBAN italiano ──────────────────────────────────
const CIN_DISPARI: Record<string, number> = {
  '0': 1,  '1': 0,  '2': 5,  '3': 7,  '4': 9,  '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  'A': 1,  'B': 0,  'C': 5,  'D': 7,  'E': 9,  'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
  'K': 2,  'L': 4,  'M': 18, 'N': 20, 'O': 11, 'P': 3,  'Q': 6,  'R': 8,  'S': 12, 'T': 14,
  'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23,
};
const CIN_PARI: Record<string, number> = {
  '0': 0,  '1': 1,  '2': 2,  '3': 3,  '4': 4,  '5': 5,  '6': 6,  '7': 7,  '8': 8,  '9': 9,
  'A': 0,  'B': 1,  'C': 2,  'D': 3,  'E': 4,  'F': 5,  'G': 6,  'H': 7,  'I': 8,  'J': 9,
  'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18, 'T': 19,
  'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25,
};

function calcolaCIN(abi: string, cab: string, conto: string): string {
  const s = abi.padStart(5, '0') + cab.padStart(5, '0') + conto.padStart(12, '0').toUpperCase();
  let sum = 0;
  for (let i = 0; i < s.length; i++) {
    sum += ((i + 1) % 2 === 1 ? CIN_DISPARI : CIN_PARI)[s[i]] ?? 0;
  }
  return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[sum % 26];
}

function generaIBAN(abi: string, cab: string, conto: string): string {
  const a = abi.padStart(5, '0');
  const c = cab.padStart(5, '0');
  const n = conto.padStart(12, '0').toUpperCase();
  const cin = calcolaCIN(a, c, n);
  const bban = cin + a + c + n; // 23 chars
  const numeric = (bban + 'IT00').split('').map(ch => {
    const code = ch.charCodeAt(0);
    return code >= 65 && code <= 90 ? (code - 55).toString() : ch;
  }).join('');
  let rem = 0;
  for (const d of numeric) rem = (rem * 10 + parseInt(d, 10)) % 97;
  const check = (98 - rem).toString().padStart(2, '0');
  return 'IT' + check + bban;
}

// Estrae il numero conto da un IBAN italiano (posizioni 12-27, no spazi)
function estraiConto(iban: string): string {
  const raw = iban.replace(/\s/g, '').toUpperCase();
  if (raw.length !== 27 || !raw.startsWith('IT')) return '';
  return raw.slice(15); // IT(2)+check(2)+CIN(1)+ABI(5)+CAB(5) = 15 chars
}

// ── Helper: badge condizioni attive ──────────────────────────────────────
function haCC(c: UltimaCondizione): boolean {
  return c.tassoDebitore != null || c.tassoCreditore != null ||
    c.speseTenutaConto != null || c.commissioneDF != null;
}
function haFido(c: UltimaCondizione): boolean {
  return c.fidoImporto != null || c.fidoTasso != null;
}
function haAnticipo(c: UltimaCondizione): boolean {
  return c.anticipoPercentuale != null || c.anticipoTasso != null || c.anticipoCommissioni != null;
}

// ── Helper: data formattata ───────────────────────────────────────────────
function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

export default function BanchePage() {
  const navigate = useNavigate();

  // ── Data ─────────────────────────────────────────────────────────────────
  const [banche, setBanche] = useState<Banca[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const {
    societaId: filterSocietaId,
    setSocietaId: setFilterSocietaId,
    listaSocieta: societaList,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  // ── Filtri ────────────────────────────────────────────────────────────────
  const [mostraDisattivate, setMostraDisattivate] = useState(false);

  // ── IBAN visibile per riga ────────────────────────────────────────────────
  const [ibanVisibile, setIbanVisibile] = useState<Set<number>>(new Set());

  // ── UX ────────────────────────────────────────────────────────────────────
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // ── Modal Nuova / Modifica Banca ─────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editBanca, setEditBanca] = useState<Banca | null>(null);
  const [formNome, setFormNome] = useState('');
  const [formAbi, setFormAbi] = useState('');
  const [formCab, setFormCab] = useState('');
  const [formConto, setFormConto] = useState('');
  const [formIban, setFormIban] = useState('');
  const [formIbanAutoGen, setFormIbanAutoGen] = useState(false);
  const [formSocietaId, setFormSocietaId] = useState('');
  const [formIbanError, setFormIbanError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // ── Gruppo contabile (padre del sottoconto banca, solo in creazione) ──────
  const [formGruppoContoId, setFormGruppoContoId] = useState('');
  const [gruppiContabili, setGruppiContabili] = useState<GruppoContabile[]>([]);
  const [gruppiLoading, setGruppiLoading] = useState(false);

  // ── Modale "Collega sottoconto" (rete di sicurezza per banche pre-esistenti) ─
  const [fixBanca, setFixBanca] = useState<Banca | null>(null);
  const [fixMode, setFixMode] = useState<'create' | 'link'>('create');
  const [fixGruppoContoId, setFixGruppoContoId] = useState('');
  const [fixGruppi, setFixGruppi] = useState<GruppoContabile[]>([]);
  const [fixContoId, setFixContoId] = useState('');
  const [fixContiDisponibili, setFixContiDisponibili] = useState<SottocontoDisponibile[]>([]);
  const [fixLoading, setFixLoading] = useState(false);
  const [fixSaving, setFixSaving] = useState(false);

  const fetchTimerRef = useRef<NodeJS.Timeout | null>(null);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Auto-fill formSocietaId when topbar/contesto ha una società attiva ──
  // isUnica copre sia "utente con 1 sola società" sia "topbar con società selezionata".
  // In entrambi i casi filterSocietaId è valorizzato e va usato come default.
  useEffect(() => {
    if (filterSocietaId) {
      setFormSocietaId(filterSocietaId);
    }
  }, [filterSocietaId]);

  // ── Fetch banche ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(fetchBanche, 200);
    return () => { if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current); };
  }, [pronta, filterSocietaId, mostraDisattivate]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchBanche() {
    if (!pronta) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSocietaId) params.set('societaId', filterSocietaId);
      if (mostraDisattivate) params.set('attiva', 'false');
      const data = await apiFetch<Banca[]>(`/api/banche?${params}`);
      setBanche(data);
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento banche', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  // ── Toggle visibilità IBAN ────────────────────────────────────────────────
  function toggleIban(id: number) {
    setIbanVisibile(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // ── Auto-genera IBAN quando ABI + CAB + conto sono presenti ─────────────
  useEffect(() => {
    if (!showModal) return;
    const a = formAbi.trim();
    const c = formCab.trim();
    const n = formConto.trim().replace(/\s/g, '');
    if (a.length === 5 && c.length === 5 && n.length > 0) {
      const iban = generaIBAN(a, c, n);
      setFormIban(formatIBANDisplay(iban));
      setFormIbanError('');
      setFormIbanAutoGen(true);
    } else if (formIbanAutoGen) {
      // Se l'utente ha tolto uno dei campi fonte, svuota solo se era auto-generato
      setFormIban('');
      setFormIbanAutoGen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formAbi, formCab, formConto, showModal]);

  // ── Carica i gruppi contabili candidati quando si apre la creazione ───────
  // Dipende dalla società scelta nel form (i gruppi sono per-società).
  useEffect(() => {
    if (!showModal || editBanca || !formSocietaId) { setGruppiContabili([]); return; }
    let cancelled = false;
    setGruppiLoading(true);
    // @api GET /api/banche/gruppi-contabili?societaId= -> [{"id":"number","codice":"string","descrizione":"string","livello":"number"}]
    apiFetch<GruppoContabile[]>(`/api/banche/gruppi-contabili?societaId=${formSocietaId}`)
      .then(data => {
        if (cancelled) return;
        setGruppiContabili(data);
        // Pre-seleziona se ce n'è uno solo; mantieni la scelta corrente se ancora valida.
        setFormGruppoContoId(prev => {
          if (data.some(g => String(g.id) === prev)) return prev;
          return data.length === 1 ? String(data[0].id) : '';
        });
      })
      .catch(() => { if (!cancelled) setGruppiContabili([]); })
      .finally(() => { if (!cancelled) setGruppiLoading(false); });
    return () => { cancelled = true; };
  }, [showModal, editBanca, formSocietaId]);

  // ── Apri modal creazione ──────────────────────────────────────────────────
  function openCreate() {
    setEditBanca(null);
    setFormNome('');
    setFormAbi('');
    setFormCab('');
    setFormConto('');
    setFormIban('');
    setFormIbanError('');
    setFormIbanAutoGen(false);
    setFormGruppoContoId('');
    setGruppiContabili([]);
    // Pre-compila societaId con quella attiva (da topbar o utente single-società).
    // Se "Tutte le società" → vuoto, l'utente deve sceglierla nel dropdown.
    setFormSocietaId(filterSocietaId || '');
    setShowModal(true);
  }

  // ── Apri modal modifica ───────────────────────────────────────────────────
  function openEdit(b: Banca) {
    setEditBanca(b);
    setFormNome(b.nome);
    setFormAbi(b.abi ?? '');
    setFormCab(b.cab ?? '');
    // Estrai il conto dall'IBAN esistente (se presente e ABI/CAB corrispondono)
    const conto = b.iban ? estraiConto(b.iban) : '';
    setFormConto(conto);
    setFormIban(b.iban ? formatIBANDisplay(b.iban) : '');
    setFormIbanError('');
    setFormIbanAutoGen(false);
    setFormSocietaId(b.societaId.toString());
    setShowModal(true);
  }

  // ── Handle IBAN input manuale (disattiva auto-gen) ───────────────────────
  function handleIbanChange(val: string) {
    const formatted = formatIBANInput(val);
    setFormIban(formatted);
    setFormIbanAutoGen(false); // l'utente ha sovrascritto manualmente
    const raw = formatted.replace(/\s/g, '');
    if (raw && !validaIBAN(raw)) {
      setFormIbanError('Formato non valido: IT + 2 cifre + 23 caratteri alfanumerici');
    } else {
      setFormIbanError('');
    }
  }

  // ── Salva banca ───────────────────────────────────────────────────────────
  async function handleSalva() {
    if (!formNome.trim()) return showToast('Il nome è obbligatorio', 'error');
    if (!formSocietaId) return showToast('Seleziona una società', 'error');
    if (!editBanca && !formGruppoContoId) return showToast('Seleziona il gruppo contabile', 'error');
    const rawIban = formIban.replace(/\s/g, '');
    if (rawIban && !validaIBAN(rawIban)) return showToast('IBAN non valido', 'error');

    setIsSaving(true);
    try {
      const body = {
        nome: formNome.trim(),
        abi: formAbi.trim() || undefined,
        cab: formCab.trim() || undefined,
        iban: rawIban || undefined,
      };

      if (editBanca) {
        const updated = await apiFetch<Banca>(`/api/banche/${editBanca.id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
        setBanche(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
        showToast('Banca aggiornata');
        setHighlightId(updated.id);
        setTimeout(() => setHighlightId(null), 2000);
      } else {
        const created = await apiFetch<Banca>('/api/banche', {
          method: 'POST', body: JSON.stringify({ ...body, societaId: parseInt(formSocietaId, 10), gruppoContoId: parseInt(formGruppoContoId, 10) }),
        });
        // La POST crea sempre il sottoconto: marca la riga come collegata senza attendere il refresh.
        setBanche(prev => [{ ...created, haSottoconto: true }, ...prev]);
        showToast('Banca creata');
        setHighlightId(created.id);
        setTimeout(() => setHighlightId(null), 2000);
      }
      setShowModal(false);
    } catch (err: any) {
      showToast(err.message || 'Errore salvataggio', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Disattiva banca ───────────────────────────────────────────────────────
  async function handleDisattiva(b: Banca) {
    if (!window.confirm(`Disattivare la banca "${b.nome}"?`)) return;
    try {
      await apiFetch(`/api/banche/${b.id}`, { method: 'DELETE' });
      setBanche(prev => prev.map(x => x.id === b.id ? { ...x, attiva: false } : x));
      showToast('Banca disattivata');
    } catch (err: any) {
      showToast(err.message || 'Errore disattivazione', 'error');
    }
  }

  // ── Apri modale "Collega sottoconto" ──────────────────────────────────────
  async function openFix(b: Banca) {
    setFixBanca(b);
    setFixGruppoContoId('');
    setFixContoId('');
    setFixGruppi([]);
    setFixContiDisponibili([]);
    setFixLoading(true);
    try {
      // @api GET /api/banche/gruppi-contabili?societaId= -> [{"id":"number","codice":"string","descrizione":"string","livello":"number"}]
      // @api GET /api/banche/:id/sottoconti-disponibili -> [{"id":"number","codice":"string","descrizione":"string"}]
      const [gruppi, conti] = await Promise.all([
        apiFetch<GruppoContabile[]>(`/api/banche/gruppi-contabili?societaId=${b.societaId}`),
        apiFetch<SottocontoDisponibile[]>(`/api/banche/${b.id}/sottoconti-disponibili`),
      ]);
      setFixGruppi(gruppi);
      setFixContiDisponibili(conti);
      // Se esistono sottoconti BANCA orfani, proponi "collega esistente"; altrimenti "crea nuovo".
      if (conti.length > 0) {
        setFixMode('link');
        setFixContoId(conti.length === 1 ? String(conti[0].id) : '');
      } else {
        setFixMode('create');
        setFixGruppoContoId(gruppi.length === 1 ? String(gruppi[0].id) : '');
      }
    } catch (err: any) {
      showToast(err.message || 'Errore caricamento dati sottoconto', 'error');
    } finally {
      setFixLoading(false);
    }
  }

  // ── Salva collegamento sottoconto ─────────────────────────────────────────
  async function handleFixSottoconto() {
    if (!fixBanca) return;
    if (fixMode === 'create' && !fixGruppoContoId) return showToast('Seleziona il gruppo contabile', 'error');
    if (fixMode === 'link' && !fixContoId) return showToast('Seleziona il sottoconto da collegare', 'error');

    setFixSaving(true);
    try {
      const body = fixMode === 'create'
        ? { gruppoContoId: parseInt(fixGruppoContoId, 10) }
        : { contoId: parseInt(fixContoId, 10) };
      // @api POST /api/banche/:id/sottoconto {"gruppoContoId":"number","contoId":"number"} -> {"id":"number","codice":"string","descrizione":"string"}
      const conto = await apiFetch<{ id: number; codice: string; descrizione: string }>(
        `/api/banche/${fixBanca.id}/sottoconto`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      setBanche(prev => prev.map(x => x.id === fixBanca.id
        ? { ...x, haSottoconto: true, contoCoge: conto }
        : x));
      showToast(`Sottoconto ${conto.codice} collegato`);
      setHighlightId(fixBanca.id);
      setTimeout(() => setHighlightId(null), 2000);
      setFixBanca(null);
    } catch (err: any) {
      showToast(err.message || 'Errore collegamento sottoconto', 'error');
    } finally {
      setFixSaving(false);
    }
  }

  // ── Colonne BopExcelTable ─────────────────────────────────────────────────
  const columns = useMemo<BopExcelColumn<Banca>[]>(() => {
    const cols: BopExcelColumn<Banca>[] = [
      {
        key: 'nome',
        header: 'Nome Banca',
        accessor: (b) => b.nome,
        filter: { type: 'text' },
        render: (b) => <span className="amministrazione-banca-nome">{b.nome}</span>,
      },
    ];
    if (!isUnica) {
      cols.push({
        key: 'societa',
        header: 'Società',
        accessor: (b) => b.societa?.nome || '',
        filter: { type: 'text' },
        width: '160px',
        cellClassName: 'bop-text-muted',
        render: (b) => b.societa?.nome,
      });
    }
    cols.push(
      {
        key: 'abiCab',
        header: 'ABI / CAB',
        accessor: (b) => `${b.abi || ''} ${b.cab || ''}`.trim(),
        filter: { type: 'text' },
        width: '140px',
        cellClassName: 'amministrazione-abi-cab',
        render: (b) => b.abi || b.cab
          ? `${b.abi ?? '—'} / ${b.cab ?? '—'}`
          : <span className="amministrazione-abi-cab-empty">—</span>,
      },
      {
        key: 'iban',
        header: 'IBAN',
        accessor: (b) => b.iban || '',
        filter: { type: 'text' },
        stopRowClick: true,
        render: (b) => {
          const ibanVisible = ibanVisibile.has(b.id);
          return b.iban ? (
            <div className="amministrazione-iban-wrapper">
              <span className="amministrazione-iban-text">
                {ibanVisible ? formatIBANDisplay(b.iban) : maskIBAN(b.iban)}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); toggleIban(b.id); }}
                className="amministrazione-iban-toggle"
                title={ibanVisible ? 'Nascondi IBAN' : 'Mostra IBAN'}
              >
                {ibanVisible ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          ) : (
            <span className="amministrazione-abi-cab-empty">—</span>
          );
        },
      },
      {
        key: 'condizioni',
        header: 'Condizioni',
        accessor: (b) => {
          const uc = b.ultimaCondizione;
          if (!uc) return '';
          const parts = [];
          if (haCC(uc)) parts.push('CC');
          if (haFido(uc)) parts.push('Fido');
          if (haAnticipo(uc)) parts.push('Ant');
          return parts.join(' ');
        },
        filter: { type: 'text' },
        width: '160px',
        headerClassName: 'bop-text-center',
        cellClassName: 'bop-text-center',
        render: (b) => {
          const uc = b.ultimaCondizione;
          return uc ? (
            <div className="amministrazione-condizioni-wrapper">
              <span title="Conto Corrente" className={`amministrazione-condizione-badge ${haCC(uc) ? 'amministrazione-condizione-cc-active' : 'amministrazione-condizione-cc-inactive'}`}>CC</span>
              <span title="Fido" className={`amministrazione-condizione-badge ${haFido(uc) ? 'amministrazione-condizione-fido-active' : 'amministrazione-condizione-fido-inactive'}`}>Fido</span>
              <span title="Anticipo Fatture" className={`amministrazione-condizione-badge ${haAnticipo(uc) ? 'amministrazione-condizione-anticipo-active' : 'amministrazione-condizione-anticipo-inactive'}`}>Ant.</span>
            </div>
          ) : (
            <span className="bop-text-muted amministrazione-condizione-badge">nessuna</span>
          );
        },
      },
      {
        key: 'ultimaVariazione',
        header: 'Ultima variazione',
        accessor: (b) => (b.ultimaCondizione?.dataDecorrenza || b.updatedAt) || '',
        filter: { type: 'dateRange' },
        width: '160px',
        cellClassName: 'amministrazione-data-text',
        render: (b) => b.ultimaCondizione ? fmtDate(b.ultimaCondizione.dataDecorrenza) : fmtDate(b.updatedAt),
      },
      {
        key: 'sottoconto',
        header: 'Sottoconto',
        accessor: (b) => b.haSottoconto ? 'OK' : 'MANCANTE',
        filter: {
          type: 'enum',
          options: [
            { value: 'OK',       label: 'Collegato' },
            { value: 'MANCANTE', label: 'Senza sottoconto' },
          ],
        },
        width: '160px',
        headerClassName: 'bop-text-center',
        cellClassName: 'bop-text-center',
        stopRowClick: true,
        render: (b) => b.haSottoconto && b.contoCoge ? (
          <span className="amministrazione-sottoconto-badge" title={b.contoCoge.descrizione}>
            {b.contoCoge.codice}
          </span>
        ) : (
          <button
            type="button"
            className="amministrazione-sottoconto-warning"
            onClick={(e) => { e.stopPropagation(); openFix(b); }}
            title="Banca operativa senza sottoconto contabile — clicca per collegarlo"
          >
            ⚠ senza sottoconto
          </button>
        ),
      },
      {
        key: 'attiva',
        header: 'Stato',
        accessor: (b) => b.attiva ? 'ATTIVA' : 'DISATTIVA',
        filter: {
          type: 'enum',
          options: [
            { value: 'ATTIVA',    label: 'Attiva' },
            { value: 'DISATTIVA', label: 'Disattiva' },
          ],
        },
        width: '110px',
        headerClassName: 'bop-text-center',
        cellClassName: 'bop-text-center',
        render: (b) => (
          <span className={`amministrazione-stato-badge ${b.attiva ? 'amministrazione-stato-attivo' : 'amministrazione-stato-disattivo'}`}>
            {b.attiva ? 'Attiva' : 'Disattiva'}
          </span>
        ),
      },
    );
    return cols;
  }, [isUnica, ibanVisibile]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout>
      {/* Toast */}
      {toast && (
        <div className={`amministrazione-toast ${toast.type === 'success' ? 'amministrazione-toast-success' : 'amministrazione-toast-error'}`}>
          {toast.msg}
        </div>
      )}

      <div className="page-header">
        <div className="page-header-text">
          <h2>Banche aziendali</h2>
          <p>Gestione banche aziendali e condizioni bancarie</p>
        </div>
        <button className="bop-btn-primary" onClick={openCreate}>+ Nuova Banca</button>
      </div>

      {/* Filtri */}
      <div className="bop-card amministrazione-filter-card">
        <div className="amministrazione-filter-wrapper">
          {!isUnica && (
            <div className="amministrazione-filter-select">
              <select
                value={filterSocietaId}
                onChange={e => setFilterSocietaId(e.target.value)}
                className="bop-select amministrazione-filter-select"
              >
                <option value="">Tutte le società</option>
                {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
          )}
          <label className="amministrazione-filter-checkbox-label">
            <input
              type="checkbox"
              checked={mostraDisattivate}
              onChange={e => setMostraDisattivate(e.target.checked)}
              className="amministrazione-filter-checkbox"
            />
            Mostra disattivate
          </label>
        </div>
      </div>

      {/* Tabella Excel-like */}
      <BopExcelTable<Banca>
        data={banche}
        loading={isLoading}
        rowKey={(b) => b.id}
        onRowClick={(b) => navigate(`/amministrazione/banche/${b.id}`)}
        initialSort={{ key: 'nome', dir: 'asc' }}
        emptyState={
          <div className="amministrazione-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="amministrazione-empty-icon">
              <rect x="3" y="10" width="18" height="11" rx="2" /><path d="M7 10V7a5 5 0 0 1 10 0v3" />
            </svg>
            <p className="amministrazione-empty-text">Nessuna banca trovata</p>
          </div>
        }
        columns={columns}
        rowActions={(b) => (
          <BopRowActions
            actions={[
              { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => openEdit(b), variant: 'primary' },
              {
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                ),
                label: 'Collega sottoconto', onClick: () => openFix(b), variant: 'primary', hidden: b.haSottoconto,
              },
              { icon: <RowIcon.Disable />, label: 'Disattiva', onClick: () => handleDisattiva(b), variant: 'danger', hidden: !b.attiva },
            ]}
          />
        )}
      />

      {/* Modal Nuova / Modifica Banca */}
      {showModal && (
        <div className="amministrazione-modal-overlay">
          <div className="amministrazione-modal-content">
            <h3 className="amministrazione-modal-title">
              {editBanca ? 'Modifica Banca' : 'Nuova Banca'}
            </h3>

            <div className="amministrazione-modal-form">
              {/* Società — solo in creazione se multi-società */}
              {!editBanca && !isUnica && (
                <div className="amministrazione-modal-form-group">
                  <label className="bop-label">Società *</label>
                  <select
                    value={formSocietaId}
                    onChange={e => setFormSocietaId(e.target.value)}
                    className="bop-select"
                  >
                    <option value="">Seleziona società...</option>
                    {societaList.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                  </select>
                </div>
              )}

              {/* Gruppo contabile — solo in creazione (il sottoconto banca viene creato qui sotto) */}
              {!editBanca && (
                <div className="amministrazione-modal-form-group">
                  <label className="bop-label">Gruppo contabile *</label>
                  <select
                    value={formGruppoContoId}
                    onChange={e => setFormGruppoContoId(e.target.value)}
                    className="bop-select"
                    disabled={gruppiLoading || !formSocietaId}
                  >
                    <option value="">{gruppiLoading ? 'Caricamento...' : 'Seleziona gruppo...'}</option>
                    {gruppiContabili.map(g => (
                      <option key={g.id} value={g.id}>{g.codice} — {g.descrizione}</option>
                    ))}
                  </select>
                  <span className="amministrazione-input-hint">
                    {!formSocietaId
                      ? 'Seleziona prima la società.'
                      : (!gruppiLoading && gruppiContabili.length === 0)
                        ? 'Nessun gruppo dello Stato Patrimoniale attivo disponibile: crealo prima nel Piano dei Conti CO.GE.'
                        : 'Il sottoconto contabile della banca verrà creato automaticamente sotto questo gruppo.'}
                  </span>
                </div>
              )}

              {/* Nome */}
              <div className="amministrazione-modal-form-group">
                <label className="bop-label">Nome Banca *</label>
                <input
                  type="text"
                  value={formNome}
                  onChange={e => setFormNome(e.target.value)}
                  placeholder="es. Banca Intesa Sanpaolo"
                  className="bop-input"
                />
              </div>

              {/* ABI / CAB */}
              <div className="amministrazione-modal-grid-2">
                <div>
                  <label className="bop-label">ABI</label>
                  <input
                    type="text"
                    value={formAbi}
                    onChange={e => setFormAbi(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="es. 03069"
                    maxLength={5}
                    className="bop-input"
                  />
                </div>
                <div>
                  <label className="bop-label">CAB</label>
                  <input
                    type="text"
                    value={formCab}
                    onChange={e => setFormCab(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="es. 01000"
                    maxLength={5}
                    className="bop-input"
                  />
                </div>
              </div>

              {/* Numero conto corrente */}
              <div className="amministrazione-modal-form-group">
                <label className="bop-label">Numero conto corrente</label>
                <input
                  type="text"
                  value={formConto}
                  onChange={e => setFormConto(e.target.value.replace(/\s/g, '').slice(0, 12).toUpperCase())}
                  placeholder="es. 000001234560"
                  maxLength={12}
                  className="bop-input amministrazione-input-monospace"
                />
                <span className="amministrazione-input-hint">
                  Max 12 caratteri alfanumerici. Inserisci ABI, CAB e conto per generare l'IBAN automaticamente.
                </span>
              </div>

              {/* IBAN */}
              <div className="amministrazione-modal-form-group">
                <label className="bop-label amministrazione-label-with-badge">
                  IBAN
                  {formIbanAutoGen && (
                    <span className="amministrazione-auto-gen-badge">
                      generato automaticamente
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={formIban}
                  onChange={e => handleIbanChange(e.target.value)}
                  placeholder="IT60 X054 2811 1010 0000 0123 456"
                  className={`bop-input amministrazione-input-monospace ${formIbanError ? 'amministrazione-input-error' : formIbanAutoGen ? 'amministrazione-input-success' : ''}`}
                />
                {formIbanError && (
                  <span className="amministrazione-error-text">
                    {formIbanError}
                  </span>
                )}
              </div>
            </div>

            <div className="amministrazione-modal-actions">
              <button className="bop-btn-secondary" onClick={() => setShowModal(false)}>Annulla</button>
              <button
                className={`bop-btn-primary ${isSaving ? 'amministrazione-btn-saving' : ''}`}
                onClick={handleSalva}
                disabled={isSaving}
              >
                {isSaving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Collega sottoconto (rete di sicurezza banche pre-esistenti) */}
      {fixBanca && (
        <div className="amministrazione-modal-overlay">
          <div className="amministrazione-modal-content">
            <h3 className="amministrazione-modal-title">Collega sottoconto — {fixBanca.nome}</h3>

            <div className="amministrazione-modal-form">
              {fixLoading ? (
                <span className="amministrazione-input-hint">Caricamento...</span>
              ) : (
                <>
                  {/* Modalità */}
                  <div className="amministrazione-modal-form-group">
                    <label className="bop-label">Modalità</label>
                    <select
                      value={fixMode}
                      onChange={e => setFixMode(e.target.value as 'create' | 'link')}
                      className="bop-select"
                    >
                      <option value="create">Crea nuovo sottoconto</option>
                      <option value="link" disabled={fixContiDisponibili.length === 0}>
                        Collega un sottoconto esistente{fixContiDisponibili.length === 0 ? ' (nessuno disponibile)' : ''}
                      </option>
                    </select>
                  </div>

                  {/* Crea nuovo: scelta gruppo padre */}
                  {fixMode === 'create' && (
                    <div className="amministrazione-modal-form-group">
                      <label className="bop-label">Gruppo contabile *</label>
                      <select
                        value={fixGruppoContoId}
                        onChange={e => setFixGruppoContoId(e.target.value)}
                        className="bop-select"
                      >
                        <option value="">Seleziona gruppo...</option>
                        {fixGruppi.map(g => (
                          <option key={g.id} value={g.id}>{g.codice} — {g.descrizione}</option>
                        ))}
                      </select>
                      <span className="amministrazione-input-hint">
                        {fixGruppi.length === 0
                          ? 'Nessun gruppo dello Stato Patrimoniale attivo disponibile: crealo prima nel Piano dei Conti CO.GE.'
                          : 'Verrà creato un nuovo sottoconto BANCA sotto questo gruppo, collegato alla banca.'}
                      </span>
                    </div>
                  )}

                  {/* Collega esistente: scelta sottoconto orfano */}
                  {fixMode === 'link' && (
                    <div className="amministrazione-modal-form-group">
                      <label className="bop-label">Sottoconto esistente *</label>
                      <select
                        value={fixContoId}
                        onChange={e => setFixContoId(e.target.value)}
                        className="bop-select"
                      >
                        <option value="">Seleziona sottoconto...</option>
                        {fixContiDisponibili.map(c => (
                          <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>
                        ))}
                      </select>
                      <span className="amministrazione-input-hint">
                        Sottoconti di sottotipo BANCA non ancora collegati ad alcuna banca.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="amministrazione-modal-actions">
              <button className="bop-btn-secondary" onClick={() => setFixBanca(null)}>Annulla</button>
              <button
                className={`bop-btn-primary ${fixSaving ? 'amministrazione-btn-saving' : ''}`}
                onClick={handleFixSottoconto}
                disabled={fixSaving || fixLoading}
              >
                {fixSaving ? 'Salvataggio...' : 'Collega'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
